import React, { useEffect, useState } from 'react';
import { brokerApi, carrierApi } from '../services/api';
import { Card } from '../components/shared/Card';
import { Badge } from '../components/shared/Badge';
import { Table } from '../components/shared/Table';
import { Alert } from '../components/shared/Alert';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { Search, AlertTriangle } from 'lucide-react';
import type { RiskAssessment, CarrierProfile } from '../types';

export const BrokerDashboard: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [searchDot, setSearchDot] = useState('');
  const [searchResults, setSearchResults] = useState<CarrierProfile | null>(null);
  const [riskAssessment, setRiskAssessment] = useState<RiskAssessment | null>(null);
  const [highRiskCarriers, setHighRiskCarriers] = useState<Array<{
    dot_number: number;
    legal_name: string;
    risk_score: number;
    alert_count: number;
  }>>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchHighRiskCarriers();
  }, []);

  const fetchHighRiskCarriers = async () => {
    try {
      const carriers = await brokerApi.getHighRiskCarriers();
      setHighRiskCarriers(carriers as unknown as Array<{
        dot_number: number;
        legal_name: string;
        risk_score: number;
        alert_count: number;
      }>);
    } catch (err) {
      console.error('Error fetching high-risk carriers:', err);
      setError('Could not load the high-risk carrier list. Search by DOT still works.');
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSearchResults(null);
    setRiskAssessment(null);

    if (!searchDot) return;

    setLoading(true);
    try {
      const [profile, risk] = await Promise.all([
        carrierApi.getProfile(parseInt(searchDot)),
        brokerApi.getCarrierRisk(parseInt(searchDot)),
      ]);

      setSearchResults(profile);
      setRiskAssessment(risk);
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Carrier not found';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="heading-2" style={{ marginBottom: 'var(--space-xl)' }}>Broker Dashboard</h1>

        {/* Search Section */}
        <Card title="Carrier Risk Assessment" icon={<Search className="w-5 h-5 text-gray-600" />}>
          <form onSubmit={handleSearch} className="flex gap-4 mb-6">
            <input
              type="text"
              value={searchDot}
              onChange={(e) => setSearchDot(e.target.value)}
              placeholder="Enter DOT Number"
              className="input-modern flex-1"
            />
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex items-center"
            >
              {loading ? <LoadingSpinner message="Searching..." size="sm" /> : <><Search className="w-4 h-4 mr-2" /> Search</>}
            </button>
          </form>

          {error && <Alert type="danger" message={error} onClose={() => setError('')} />}

          {searchResults && riskAssessment && (
            <div className="space-y-6">
              {/* Carrier Info */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-4">{searchResults.legalName}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">DOT Number</p>
                    <p className="font-semibold">{searchResults.dotNumber}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <p className="font-semibold">{searchResults.statusCode === 'A' ? 'Active' : 'Inactive'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Safety Rating</p>
                    <p className="font-semibold">{searchResults.safetyRating || 'Not Rated'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Fleet Size</p>
                    <p className="font-semibold">{searchResults.totalDrivers} drivers, {searchResults.totalPowerUnits} vehicles</p>
                  </div>
                </div>
              </div>

              {/* Risk Assessment */}
              <div className="card-modern">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold">Risk Assessment</h3>
                  <div className="flex items-center space-x-4">
                    <div className={`font-serif text-5xl ${
                      riskAssessment.riskLevel === 'low' ? 'text-green-600' :
                      riskAssessment.riskLevel === 'medium' ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>
                      {riskAssessment.riskScore}
                    </div>
                    <div>
                      <Badge variant={
                        riskAssessment.riskLevel === 'low' ? 'success' :
                        riskAssessment.riskLevel === 'medium' ? 'warning' :
                        'danger'
                      } size="lg">
                        {riskAssessment.riskLevel.toUpperCase()} RISK
                      </Badge>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900 mb-2">Risk Factors:</h4>
                  {riskAssessment.factors.map((factor, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                      <div className="flex-1">
                        <p className="font-medium">{factor.category}</p>
                        <p className="text-sm text-gray-600">{factor.description}</p>
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-2xl font-bold text-gray-900">{factor.score}</p>
                        <p className="text-xs text-gray-500">Weight: {factor.weight}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-4 bg-gray-100 rounded-lg">
                  <p className="font-semibold text-gray-900 mb-2">Recommendation:</p>
                  <p className="text-gray-700">{riskAssessment.recommendation}</p>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* High Risk Carriers Table */}
        <div className="mt-8">
          <Card title="High-Risk Carriers" icon={<AlertTriangle className="w-5 h-5 text-red-600" />}>
            <Table
              data={highRiskCarriers}
              keyExtractor={(carrier) => carrier.dot_number}
              columns={[
                { key: 'dot_number', header: 'DOT Number' },
                { key: 'legal_name', header: 'Company Name' },
                {
                  key: 'risk_score',
                  header: 'Risk Score',
                  render: (carrier) => (
                    <span className={`font-bold ${
                      carrier.risk_score >= 70 ? 'text-red-600' :
                      carrier.risk_score >= 40 ? 'text-yellow-600' :
                      'text-green-600'
                    }`}>
                      {carrier.risk_score}
                    </span>
                  )
                },
                {
                  key: 'alert_count',
                  header: 'Alerts',
                  render: (carrier) => (
                    <Badge variant="danger" size="sm">{carrier.alert_count}</Badge>
                  )
                },
              ]}
              emptyMessage="No high-risk carriers found"
            />
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BrokerDashboard;
